import '@/config/axios-interceptor';
import { registerAppGlobalContext } from '@/utils/app-global-context';
import { isNetworkConnected } from '@/utils/network-status';
import { MenuButton } from '@/model/menubutton.model';
import { appSectionKeyByRouteName } from '@/model/app-section.registry';
import AccountService from '@/services/account.service';
import { appSectionService } from '@/services/app-section.service';
import CalendarService from '@/services/calendar.service';
import { RmxAudioPlayer } from "capacitor-plugin-playlist";
import QrcodeVue from 'qrcode.vue';
import { createApp, reactive } from 'vue';
import VueSlider from 'vue-slider-component';
import App from './App.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import FilesystemCustomUtils from '@/services/filesystem.custom.utils';
import { PlayerBar } from './model/playerbar.model';
import { Release } from './model/release.model';
import router from './router';
import store from './store/index';

import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { IonicVue } from '@ionic/vue';

// @ts-ignore
import MittEmitter from 'mitt-emitter';


/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/display.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';

/* Theme variables */
import './theme/liga-slider.css';
import './theme/variables.css';
import { getCurrentAppearancePreference, initAppearance } from '@/services/appearance.service';
import { attachDeepLinkHandlersEarly, markDeepLinkRouterReady } from '@/services/deep-link.service';
import { attachPushOpenHandlersEarly } from '@/services/push.service';

const globalContext = new GlobalContext();
registerAppGlobalContext(globalContext);
appSectionService.setGlobalContext(globalContext);
const accountService = new AccountService(globalContext);
const calendarService = new CalendarService();
calendarService.globalContext = globalContext;
const menuButton = new MenuButton('left');
const playerBar = new PlayerBar();
const release = new Release();
const emitter = new MittEmitter();
globalContext.isMobile = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';

Network.getStatus().then((status) => {
  if (!status.connected) {
    globalContext.offlineMode = true;
  }
  if (globalContext.isMobile) {
    const fsUtils = new FilesystemCustomUtils();
    void fsUtils.hasAnyLibraryMusicOffline().then((has) => {
      globalContext.hasOfflineLibraryMusic = has;
    });
  }
})

const app = createApp(App)
  .use(IonicVue, { swipeBackEnabled: true, backButtonText: 'Назад' })
  .use(router)
  .use(store)
  .component('VueSlider', VueSlider)
  .component('QrcodeVue', QrcodeVue)
  .provide(
    'globalContext', globalContext
  )
  .provide(
    'accountService', reactive(accountService),
  )
  .provide(
    'calendarService', reactive(calendarService),
  )
  .provide(
    'appSectionService', reactive(appSectionService),
  )
  .provide(
    'menuButton', reactive(menuButton),
  )
  .provide(
    'playerBar', reactive(playerBar),
  )
  .provide(
    'release', reactive(release),
  )
  .provide(
    'emitter', emitter
  );

if (globalContext.isMobile) {
  try {
    const audioPlayer = new RmxAudioPlayer();
    audioPlayer.initialize();
    audioPlayer.setLoop(false);
    audioPlayer.setOptions({
      resetStreamOnPause: false,
      options: {
        icon: "icon",
      },
    });
    app.provide('audioPlayer', audioPlayer);
  } catch (e) {
    console.warn('RmxAudioPlayer init failed', e);
  }
}

void (async () => {
  const effective = await initAppearance();
  globalContext.appearance = getCurrentAppearancePreference();
  globalContext.effectiveTheme = effective;

  // До mount: поймать cold-start URL (getLaunchUrl) и не потерять appUrlOpen.
  if (globalContext.isMobile) {
    void attachDeepLinkHandlersEarly();
    void attachPushOpenHandlersEarly();
  }

  await router.isReady();
  app.mount('#app');
  markDeepLinkRouterReady();
})();

router.beforeEach(async (to, from, next) => {
  const adminRoutes = ['Users', 'AdminRoles', 'AdminAppSections', 'AdminEducationSections', 'AdminVideoUpload', 'AdminMediaUpload', 'AdminReleases', 'AdminReleaseContent', 'AdminLibraryMusic', 'AdminReturns', 'AdminMediaStats', 'AdminSubscriptionsList', 'AdminSubscriptionNew', 'AdminSubscriptionEdit', 'AdminSubscriptionPurchases', 'AdminPromoCodesList', 'AdminPromoCodeNew', 'AdminPromoCodeEdit', 'AdminConversionQueue'];

  if (
    to.name === 'BadConnection' ||
    to.name === 'SignUp' ||
    to.name === 'ResetPassword' ||
    to.name === 'Login' ||
    to.name === 'PrivacyPolicy' ||
    to.name === 'AccountDeletion' ||
    to.name === 'OfferSupplement' ||
    to.name === 'Showcase' ||
    to.name === 'Settings' ||
    to.name === 'MessengerInviteJoin'
  ) {
    if (to.name === 'BadConnection') {
      globalContext.prevRoute = from.fullPath;
    }
    next();
    return;
  }

  if (adminRoutes.includes(to.name as string)) {
    if (!accountService.authenticated && !globalContext.offlineMode) {
      try {
        const restored = await accountService.retrieveAccount();
        if (!restored) {
          const onlineAdmin = await isNetworkConnected();
          next(onlineAdmin ? { name: 'Showcase' } : { name: 'Login' });
          return;
        }
      } catch (e) {
        console.error('Restore session:', e);
        const onlineAdminCatch = await isNetworkConnected();
        next(onlineAdminCatch ? { name: 'Showcase' } : { name: 'Login' });
        return;
      }
    }
    try {
      const hasAccess = await accountService.hasAnyAuthorityAndCheckAuth(['ROLE_ADMIN', 'ROLE_MANAGER']);
      if (hasAccess) {
        next();
      } else {
        console.warn(`Access denied to ${String(to.name)}: admin or manager role required`);
        next('/showcase');
      }
    } catch (err) {
      console.error('Error checking access:', err);
      const onlineAdminErr = await isNetworkConnected();
      next(onlineAdminErr ? { name: 'Showcase' } : { name: 'Login' });
    }
    return;
  }

  if (
    to.name !== 'Login' &&
    to.name !== 'PrivacyPolicy' &&
    to.name !== 'AccountDeletion' &&
    to.name !== 'OfferSupplement' &&
    to.name !== 'Showcase' &&
    to.name !== 'Settings' &&
    !accountService.authenticated &&
    !globalContext.offlineMode
  ) {
    try {
      const restored = await accountService.retrieveAccount();
      if (!restored || !accountService.authenticated) {
        const online = await isNetworkConnected();
        next(online ? { name: 'Showcase' } : { name: 'Login' });
        return;
      }
    } catch (e) {
      console.error('Restore session:', e);
      const online = await isNetworkConnected();
      next(online ? { name: 'Showcase' } : { name: 'Login' });
      return;
    }
  }

  const sectionKey = appSectionKeyByRouteName(to.name as string | undefined);
  if (sectionKey) {
    try {
      await appSectionService.ensureLoaded();
      if (appSectionService.canAccessSection(sectionKey)) {
        next();
      } else {
        console.warn(`Access denied to ${String(to.name)}: app section ${sectionKey} is restricted`);
        next('/showcase');
      }
    } catch (err) {
      console.error('Error checking app section access:', err);
        next('/showcase');
    }
    return;
  }

  next();
});